import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { WrappedFieldArrayProps } from 'redux-form';
import { Field } from 'redux-form';

import type ContactRM from 'ab-requestModels/contact/upsert';

import { PhoneTypeNames, PhoneTypes } from '@acceligentllc/shared/enums/contactMethodType';
import { CountryCode } from '@acceligentllc/shared/enums/countryCode';
import { UpsertContactStatus } from '@acceligentllc/shared/enums/upsertContactStatus';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';

import styles from './styles.module.scss';

interface PhoneTypeOption {
	id: PhoneTypes;
	label: string;
}

export interface OwnProps {
	customError?: string;
	onRemoveContactMethod: (email: ContactRM['phones'][0]) => void;
	change: (fieldName: string, value: UpsertContactStatus) => void;
}

type Props = OwnProps & WrappedFieldArrayProps<ContactRM['phones'][0]>;

class PhoneContactMethods extends React.Component<Props> {

	static _phoneTypeOptions: PhoneTypeOption[] = Object.keys(PhoneTypes)
		.map((_key: PhoneTypes) => ({ id: _key, label: PhoneTypeNames[_key] }));

	addContactMethod = () => {
		const { fields } = this.props;

		fields.push({
			type: PhoneTypes.PHONE_DIRECT,
			countryCode: CountryCode.US,
			status: UpsertContactStatus.ADDED,
		} as ContactRM['phones'][0]);
	};

	updateContactMethod = (index: number, fieldName: string) => {
		const { change, fields } = this.props;
		const field = fields.get(index);
		if (field.status === UpsertContactStatus.OLD) {
			change(`${fieldName}.status`, UpsertContactStatus.EDITED);
		}
	};

	removeContactMethod = (index: number) => {
		const { onRemoveContactMethod, fields } = this.props;

		const removedMethod = fields.get(index);
		fields.remove(index);
		if (removedMethod.status !== UpsertContactStatus.ADDED) {
			onRemoveContactMethod({ ...removedMethod, status: UpsertContactStatus.REMOVED });
		}
	};

	renderMethodTypes = () => {
		const { fields } = this.props;

		return fields.map((_field: string, _index: number) => (
			<Row key={_index}>
				<Col sm={6}>
					<Field
						component={Input}
						name={`${_field}.value`}
						onValueChange={this.updateContactMethod.bind(this, _index, _field)}
						placeholder="Number"
						type="tel"
					/>
				</Col>
				<Col sm={6}>
					<Field
						component={Dropdown}
						id={_field}
						labelKey="label"
						name={`${_field}.type`}
						onValueChange={this.updateContactMethod.bind(this, _index, _field)}
						options={PhoneContactMethods._phoneTypeOptions}
						placeholder="Type"
						valueKey="id"
						withCaret={true}
					/>
				</Col>
				<Col sm={6}>
					<Field
						component={Input}
						id={_field}
						name={`${_field}.extension`}
						onValueChange={this.updateContactMethod.bind(this, _index, _field)}
						placeholder="Extension"
					/>
				</Col>
				<Col className={styles.deleteWrapper} sm={1}>
					<Button
						icon="Delete"
						onClick={this.removeContactMethod.bind(this, _index)}
						style="link-danger"
						tooltip="Delete"
					/>
				</Col>
			</Row>
		));
	};

	render() {
		const { meta: { error, submitFailed }, customError, fields } = this.props;

		return (
			<div className="contact-method contact-method--non-padded">
				<div className="contact-method__header m-t-m m-b-m">
					<SegmentLabel
						label="Mobile Phones"
						tooltip="Phone numbers associated with this contact, including type (e.g., Mobile, Office) and extension where applicable."
					/>
				</div>
				{!!fields.length &&
					<div className="contact-method__header">
						Mobile Phone
					</div>
				}
				{this.renderMethodTypes()}
				<Row>
					<Col sm={12}>
						<Button
							icon="ActionPlusSmall"
							label="Add Mobile Phone"
							onClick={this.addContactMethod}
							style="link"
						/>
						{submitFailed && error && <span>{error}</span>}
						{customError && <span className="help-block"><span className="icon-info"></span>{customError}</span>}
					</Col>
				</Row>
			</div>
		);
	}
}

export default PhoneContactMethods;
